import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["borderRadius", "disabled", "children", "isOpen", "onToggle", "transitionDuration"],
  _excluded2 = ["as", "children", "gap", "position"],
  _excluded3 = ["as", "children"],
  _excluded4 = ["children"];
import { createContext, useContext } from 'react';
import styled from 'styled-components';
import { RightIcon } from 'foundations-assets';
import { LayoutBox } from '../Layout/LayoutBox';
import { LayoutHorizontal } from '../Layout/LayoutHorizontal';
import { useDisclosure } from '../useDisclosure';
import { IconContainer } from '../Icons/IconContainer';
import { TransitionCollapse } from '../Transitions';
import { jsx as _jsx } from "react/jsx-runtime";
export const CollapsibleContext = /*#__PURE__*/createContext({
  disabled: false,
  isOpen: false,
  onToggle: () => {},
  transitionDuration: 300
});
export const Collapsible = _ref => {
  let {
      borderRadius,
      disabled,
      children,
      isOpen,
      onToggle: providedOnToggle,
      transitionDuration
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    isOpen: isOpenUncontrolled,
    onToggle
  } = useDisclosure({
    isOpen,
    onOpen: () => providedOnToggle === null || providedOnToggle === void 0 ? void 0 : providedOnToggle(true),
    onClose: () => providedOnToggle === null || providedOnToggle === void 0 ? void 0 : providedOnToggle(false)
  });
  return /*#__PURE__*/_jsx(CollapsibleContext.Provider, {
    value: {
      borderRadius,
      disabled: disabled !== null && disabled !== void 0 ? disabled : false,
      isOpen: isOpenUncontrolled,
      onToggle,
      transitionDuration
    },
    children: /*#__PURE__*/_jsx(LayoutBox, Object.assign({}, rest, {
      borderRadius: borderRadius,
      overflow: isOpen ? 'visible' : 'hidden',
      children: children
    }))
  });
};
const HeaderToggle = styled(LayoutHorizontal).withConfig({
  displayName: "Collapsible__HeaderToggle",
  componentId: "sc-1gsvaie-0"
})(["cursor:", ";"], ({
  isDisabled
}) => isDisabled ? 'not-allowed' : 'pointer');
const Header = _ref2 => {
  let {
      as = 'header',
      children,
      gap = 'space',
      position = 'relative'
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    borderRadius,
    disabled,
    onToggle
  } = useContext(CollapsibleContext);
  const handleToggle = () => {
    if (disabled) {
      return;
    }
    onToggle();
  };
  return /*#__PURE__*/_jsx(HeaderToggle, Object.assign({}, rest, {
    borderRadiusStartEnd: borderRadius,
    borderRadiusStartStart: borderRadius,
    flexGrow: 1,
    forwardedAs: as,
    gap: gap,
    isDisabled: disabled,
    onClick: handleToggle,
    opacity: disabled ? 0.5 : 1,
    position: position,
    role: "button",
    tabIndex: 0,
    children: children
  }));
};
Collapsible.Header = Header;
const IconRotate = styled(IconContainer).withConfig({
  displayName: "Collapsible__IconRotate",
  componentId: "sc-1gsvaie-1"
})(["transform:", ";transition:transform 0.3s ease-in-out;"], ({
  isOpen
}) => isOpen ? 'rotate(90deg)' : 'rotate(0deg)');
const Toggle = _ref3 => {
  let rest = Object.assign({}, (_objectDestructuringEmpty(_ref3), _ref3));
  const {
    isOpen
  } = useContext(CollapsibleContext);
  return /*#__PURE__*/_jsx(IconRotate, Object.assign({}, rest, {
    isOpen: isOpen,
    children: /*#__PURE__*/_jsx(RightIcon, {})
  }));
};
Collapsible.Toggle = Toggle;
const Footer = _ref4 => {
  let {
      as = 'footer',
      children
    } = _ref4,
    rest = _objectWithoutPropertiesLoose(_ref4, _excluded3);
  const {
    borderRadius
  } = useContext(CollapsibleContext);
  return /*#__PURE__*/_jsx(LayoutBox, Object.assign({}, rest, {
    as: as,
    borderRadiusEndEnd: borderRadius,
    borderRadiusEndStart: borderRadius,
    children: children
  }));
};
Collapsible.Footer = Footer;
const Content = _ref5 => {
  let {
      children
    } = _ref5,
    rest = _objectWithoutPropertiesLoose(_ref5, _excluded4);
  const {
    isOpen,
    transitionDuration
  } = useContext(CollapsibleContext);
  return /*#__PURE__*/_jsx(TransitionCollapse, {
    isVisible: isOpen,
    transitionDuration: transitionDuration,
    children: /*#__PURE__*/_jsx(LayoutBox, Object.assign({}, rest, {
      children: isOpen ? /*#__PURE__*/_jsx(LayoutBox, {
        children: children
      }) : null
    }))
  });
};
Collapsible.Content = Content;